import { useCallback, useEffect, useState } from 'react';
import { httpClient, ROOT_URL } from '../../App';
import { useNotification } from '@refinedev/core';
import { getErrorMessage } from '../../api/error';
import { User } from '../../interfaces/users';

export const useCustomer = (id?: number, enabled = true) => {
  const [data, setData] = useState<User>();
  const [isLoading, setIsLoading] = useState(false);
  const { open: openNotification } = useNotification();

  const fetch = useCallback(async () => {
    if (!Number.isInteger(id)) {
      return null;
    }
    setIsLoading(true);
    try {
      const result = await httpClient.get(`${ROOT_URL}/api/v1/users/${id}`);
      if (result.status === 200) {
        setData(result.data);
      }
    } catch (error) {
      const message = getErrorMessage(error);
      setData(undefined);
      if (openNotification) {
        openNotification({
          key: 'edit_failure',
          message: `Error loading company #${id}`,
          description: message || 'Something went wrong',
          type: 'error',
        });
      }
    }
    setIsLoading(false);
  }, [id, openNotification]);

  const clearData = useCallback(() => {
    setData(undefined);
  }, []);

  useEffect(() => {
    if (!enabled || id === undefined) {
      if (id === undefined) {
        clearData();
      }
      return;
    }
    fetch();
  }, [enabled, clearData, fetch, id]);

  return {
    data,
    fetch,
    isLoading,
    clearData,
  };
};

export const getStorageUrl = async (uuid: string): Promise<string | null> => {
  if (!uuid) return null;
  try {
    const result = await httpClient.get(`${ROOT_URL}/api/v1/storage/get-url/${uuid}`);
    const url = result.data?.data?.url;
    if (result.status === 200 && url) {
      return url;
    }
    return typeof result.data?.data === 'string' ? result.data.data : null;
  } catch {
    return null;
  }
};
